import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import { GameStateTransport, type GameStateConnectionConfig, type GameStateConnectionStatus,
  type GameStateSocket } from '@/lib/gameState/transport';
import { coreGameStateKey, parseGameIdentity, type AdmittedGameState, type GameStateComparison } from '@/lib/gameState/admission';
import { emptyGameStateDiagnostics, GameStateDiagnosticJournal, type GameStateDiagnostics } from '@/lib/gameState/diagnostics';
import { releaseBody } from '@/lib/infra/responseBody';
import { feedFilterGameStateEnabled } from '@/clients/feedFilterConfig';
import { cachedDirectoryLanes, type DirectoryLanes } from '@/clients/directoryLanes';
import { directoryLaneToken } from '@/clients/feedConfig';

export interface ResolvedGameStateConfig extends GameStateConnectionConfig {
  /** From the directory's `lanes.gamestate`: primary readers are on. */
  fromDirectory: boolean;
}

/** DIRECTORY FIRST (directory-edgelike-cores D3/D4, workspace dc20d5f): a
 * cached `lanes.gamestate` of exactly one valid entry is the base URL, with
 * the matched configured core's token else VITE_HYDRA_CORE_TOKEN, and turns
 * primary readers on whatever VITE_HYDRA_GAMESTATE_PRIMARY says (Dan: no site
 * env edits). Anything else — absent, empty, two or more entries, an invalid
 * URL (`parseLanes` drops them) — runs the env path below unchanged.
 *
 * ENV: the override is a base URL, not the legacy analytics base. An absent
 * override leaves legacy scores/consensus alone and opens no Core socket.
 * Never borrow the analytics bearer for an explicitly configured Core.
 */
export function gameStateConfig(env: Record<string, unknown>, lanes: DirectoryLanes = {}): ResolvedGameStateConfig | null {
  const lane = lanes.gamestate?.length === 1 ? lanes.gamestate[0] : undefined;
  if (lane) return { baseUrl: lane.url, token: directoryLaneToken(lane.url, env) ?? '', fromDirectory: true };
  const baseUrl = typeof env.VITE_HYDRA_GAMESTATE_URL === 'string' ? env.VITE_HYDRA_GAMESTATE_URL.trim() : '';
  if (!baseUrl) return null;
  const token = [env.VITE_HYDRA_GAMESTATE_TOKEN, env.VITE_HYDRA_CORE_TOKEN]
    .find(value => typeof value === 'string' && value.trim()) as string | undefined;
  return { baseUrl, token: token?.trim() ?? '', fromDirectory: false };
}
// The cached lanes are read ONCE, at import: the transport keeps one endpoint
// for the page's life, so a lane change applies from a tab's second load
// after it (D3) — never mid-page, even after this load's fetch rewrites the
// cache.
const bootLanes = cachedDirectoryLanes();
const config = gameStateConfig(import.meta.env, bootLanes);

/** This page's game-state lane (null: no Core lane) — the import-time lanes,
 *  else env. Display surfaces read this, never a fresh cache read. */
export function coreGameStateConfig(): ResolvedGameStateConfig | null {
  return gameStateConfig(import.meta.env, bootLanes);
}

/** True when this page's game state comes from the directory's lane — which
 *  turns primary readers on (D3). Cheap: primary readers call it per row. */
export function coreGameStateFromDirectory(): boolean {
  return bootLanes.gamestate?.length === 1;
}

interface CoreGameStatesView {
  configured: boolean;
  status: GameStateConnectionStatus;
  error: string | null;
  records: ReadonlyMap<string, AdmittedGameState>;
  comparisons: ReadonlyMap<string, GameStateComparison>;
  diagnostics: GameStateDiagnostics;
}
export const useCoreGameStatesStore = create<CoreGameStatesView>(() => ({
  configured: config !== null, status: 'stopped', error: null, records: new Map(), comparisons: new Map(),
  diagnostics: emptyGameStateDiagnostics(),
}));

/** The Core record a g1 game key names. Core stores college football under
 *  `ncaaf|…`; g1's board rows, URLs and order keys may say `cfb|…`
 *  (directory-lanes-main DNL-001). Every Core lookup goes through here, as
 *  every interest goes through `useCoreGameStateInterests`: `coreKey` is the
 *  key the record is filed and checked under, so a caller compares
 *  `row.state.gameKey` against it, and keeps its OWN key for anything g1-side
 *  (marketStore's start-time slot, display identity). Other sports pass
 *  through unchanged. `null` for an empty key. */
export function lookupCoreGameState(records: ReadonlyMap<string, AdmittedGameState>,
  key: string | null | undefined): { coreKey: string; row: AdmittedGameState | undefined } | null {
  const coreKey = coreGameStateKey(key);
  return coreKey ? { coreKey, row: records.get(coreKey) } : null;
}

let journal = new GameStateDiagnosticJournal();
let diagnosticScope = currentAuthScope();
// History must also clear when auth changes after the last comparison reader
// unmounted; the transport's shorter-lived auth subscription cannot do that.
const removeDiagnosticAuth = useAuthStore.subscribe(() => {
  const scope = currentAuthScope();
  if (scope === diagnosticScope) return;
  diagnosticScope = scope;
  journal = new GameStateDiagnosticJournal();
  useCoreGameStatesStore.setState({ diagnostics: emptyGameStateDiagnostics() });
});
if (import.meta.hot) import.meta.hot.dispose(removeDiagnosticAuth);

const interests = new Map<symbol, readonly string[]>();
const interestedKeys = () => [...new Set([...interests.values()].flat())].sort();
let owner: GameStateTransport | null = null;
let authScope: string | null = null;
let removeVisibility: (() => void) | null = null;
let removeAuth: (() => void) | null = null;

/** How long the owner outlives its last reader. A route change, or a board
 *  whose rows have not arrived yet, leaves no reader for a moment; tearing
 *  the owner down there wiped every record and reopened the socket, so each
 *  live game's Date and Time/Score cells read UNKNOWN until the next snapshot
 *  landed. A reader that returns within the grace keeps the live owner. */
export const READER_GRACE_MS = 10_000;
let idleTimer: ReturnType<typeof setTimeout> | null = null;

function clearIdle(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
}

function clearOwner(): void {
  clearIdle();
  owner?.stop();
  owner = null;
  removeVisibility?.(); removeVisibility = null;
  removeAuth?.(); removeAuth = null;
  authScope = null;
  useCoreGameStatesStore.setState({ status: 'stopped', error: null, records: new Map(), comparisons: new Map(),
    diagnostics: journal.observe('stopped', new Map(), new Map(), Date.now()) });
}

function socketFor(url: string): GameStateSocket {
  const ws = new WebSocket(url);
  const socket: GameStateSocket = { onopen: null, onmessage: null, onerror: null, onclose: null,
    send: data => ws.send(data), close: () => ws.close() };
  ws.onopen = () => socket.onopen?.();
  ws.onmessage = event => socket.onmessage?.({ data: event.data });
  ws.onerror = () => socket.onerror?.();
  ws.onclose = () => socket.onclose?.();
  return socket;
}

function currentAuthScope(): string | null {
  const auth = useAuthStore.getState();
  return auth.status === 'authenticated' && auth.user
    ? JSON.stringify([auth.user.id, auth.user.orgId, auth.user.role]) : null;
}

function reconcile(): void {
  const nextScope = currentAuthScope();
  if (!config || !nextScope || (owner && authScope !== nextScope && !interests.size)) { clearOwner(); return; }
  if (!interests.size) {
    // No reader right now: stop polling the departed keys, keep the socket
    // and its records through the grace.
    owner?.setKeys([]);
    if (owner && !idleTimer) idleTimer = setTimeout(() => { idleTimer = null; if (!interests.size) clearOwner(); }, READER_GRACE_MS);
    return;
  }
  clearIdle();
  if (owner && authScope === nextScope) { owner.setKeys(interestedKeys()); return; }
  clearOwner();
  authScope = nextScope;
  try {
    owner = new GameStateTransport({ config, keys: interestedKeys(), socket: socketFor,
      // odds-gamestate-feed-filters D6: `{games: <the readers' keys>}` on the
      // socket instead of every record — behind VITE_FEED_FILTERS_GAMESTATE
      // (default off), read when the owner is built.
      filter: feedFilterGameStateEnabled(),
      read: async (url, init) => {
        const response = await fetch(url, init);
        if (!response.ok) { releaseBody(response); throw new Error('GameState comparison unavailable'); }
        return response.json();
      },
      visible: () => !document.hidden,
      changed: transport => {
        if (currentAuthScope() !== nextScope || authScope !== nextScope) return;
        const records = transport.admission.snapshot();
        const comparisons = transport.admission.comparisons();
        useCoreGameStatesStore.setState({ status: transport.status, error: transport.error,
          records, comparisons, diagnostics: journal.observe(transport.status, records, comparisons, Date.now()) });
      },
    });
    const onVisibility = () => { if (!document.hidden) owner?.refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    removeAuth = useAuthStore.subscribe(() => {
      if (currentAuthScope() !== authScope) reconcile();
    });
    owner.start();
  } catch {
    clearOwner();
    useCoreGameStatesStore.setState({ status: 'disconnected', error: 'GameState configuration unavailable',
      diagnostics: journal.observe('disconnected', new Map(), new Map(), Date.now()) });
  }
}

/** One gamestate-only socket and one unioned batch-poll owner per application,
 * independent of the market feed's direct/SharedWorker mode. No per-game
 * transport. Removing the final reader tears down all timers and listeners.
 */
export function useCoreGameStateInterest(key: string | null): void {
  useCoreGameStateInterests(key ? [key] : []);
}

/** Dashboard lists and detail panels contribute to the same union. Stable
 * key content avoids tearing down a reader on an ordinary parent render, and
 * a changed key set re-scopes this reader IN PLACE: the board is often the
 * page's only reader, and a remove-then-add on every new row emptied the
 * union between the two. */
export function useCoreGameStateInterests(keys: readonly string[]): void {
  const inputKeys = JSON.stringify(keys);
  const encodedKeys = useMemo(() => {
    if (!config) return '[]';
    const candidates: string[] = JSON.parse(inputKeys);
    // Asked in Core's spelling (cfb → ncaaf), so a board row and a Game
    // Detail page on the same game are one interest.
    const coreKeys = candidates.map(key => coreGameStateKey(key)).filter((key): key is string => !!key);
    return JSON.stringify([...new Set(coreKeys.filter(key => parseGameIdentity(key)))].sort());
  }, [inputKeys]);
  const auth = useAuthStore(s => s.status === 'authenticated' ? s.user : null);
  const scope = auth ? JSON.stringify([auth.id, auth.orgId, auth.role]) : null;
  const [id] = useState(() => Symbol('core-game-state-reader'));
  useEffect(() => {
    const admittedKeys: string[] = JSON.parse(encodedKeys);
    if (!config || !scope || !admittedKeys.length) {
      if (interests.delete(id)) reconcile();
      return;
    }
    interests.set(id, admittedKeys);
    reconcile();
  }, [id, encodedKeys, scope]);
  useEffect(() => () => { interests.delete(id); reconcile(); }, [id]);
}
